import { useState } from 'react';
import { Card } from '../ui/Card';
import { fmtDollar, fmtDollarInt } from '../../lib/format';
import type { BuyerProfile } from '../../lib/types';

interface ScenarioRow {
  profile: BuyerProfile;
  monthlyPayment: number;
  cashToClose: number;
  frontDTIPct: number | null;
  payIndicator: 'green' | 'yellow' | 'red';
  cashIndicator: 'green' | 'yellow' | 'red';
  isCurrentProfile: boolean;
}

interface Props {
  rows: ScenarioRow[];
  onOpenCalculator?: () => void;
  onAnalyzeWithProfile?: (profileId: string) => Promise<void>;
}

function indicatorClass(indicator: 'green' | 'yellow' | 'red'): string {
  return `indicator indicator-${indicator}`;
}

export function ScenarioComparisonCard({ rows, onOpenCalculator, onAnalyzeWithProfile }: Props) {
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);

  const handleAnalyze = async (profileId: string) => {
    if (!onAnalyzeWithProfile || analyzingId) return;
    setAnalyzingId(profileId);
    try {
      await onAnalyzeWithProfile(profileId);
    } catch {
      setAnalyzingId(null);
    }
  };

  return (
    <Card title="Scenario Comparison">
      {rows.length === 1 && (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          Create additional buyer profiles to compare scenarios (e.g. solo buyer, with partner, with 401k loan).
        </p>
      )}
      <div className="table-scroll-wrap">
      <table className="price-levels-table">
        <thead>
          <tr>
            <th>Scenario</th>
            <th style={{ textAlign: 'right' }}>Monthly Payment</th>
            <th style={{ textAlign: 'right' }}>Front DTI</th>
            <th style={{ textAlign: 'right' }}>Cash to Close</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.profile.id} className={row.isCurrentProfile ? 'price-level-at-asking' : ''}>
              <td>
                <div className="level-label" style={{ fontWeight: row.isCurrentProfile ? 700 : 400 }}>
                  {row.profile.name}
                  {row.isCurrentProfile && (
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.35rem' }}>
                      (current)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  {fmtDollarInt(row.profile.grossAnnualIncome + (row.profile.coBorrower?.grossAnnualIncome ?? 0))}/yr
                </div>
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className={indicatorClass(row.payIndicator)} />
                {fmtDollar(row.monthlyPayment)}
              </td>
              <td style={{ textAlign: 'right' }}>
                {row.frontDTIPct !== null ? (
                  <span style={{
                    color: row.frontDTIPct > 31 ? 'var(--red)' : row.frontDTIPct > 28 ? 'var(--yellow, #f59e0b)' : 'inherit',
                    fontWeight: row.frontDTIPct > 31 ? 600 : 400,
                  }}>
                    {row.frontDTIPct.toFixed(1)}%
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className={indicatorClass(row.cashIndicator)} />
                {fmtDollarInt(row.cashToClose)}
                {row.cashIndicator === 'red' && row.profile.cashOnHand > 0 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--red)' }}>
                    Gap: {fmtDollarInt(row.cashToClose - row.profile.cashOnHand)}
                  </div>
                )}
              </td>
              <td>
                {row.isCurrentProfile && onOpenCalculator && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={onOpenCalculator}
                  >
                    Calculator →
                  </button>
                )}
                {!row.isCurrentProfile && onAnalyzeWithProfile && (
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={analyzingId !== null}
                    onClick={() => handleAnalyze(row.profile.id)}
                  >
                    {analyzingId === row.profile.id ? '…' : 'Analyze →'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <span><span className="indicator indicator-green" /> Comfortable</span>
        <span><span className="indicator indicator-yellow" /> Tight</span>
        <span><span className="indicator indicator-red" /> Over budget</span>
        <span style={{ marginLeft: 'auto' }}>At asking price · default assumptions</span>
      </div>
    </Card>
  );
}
